import _ from "lodash";
import { useRef, useState } from "react";
import { Box, Button, Header, SpaceBetween, Toggle } from "@amzn/awsui-components-react";
import { SelectProps } from "@amzn/awsui-components-react/polaris/select";
import { getString } from "../../../common/ui-string-labels/ui-strings-utils";

import { SelectionState } from "../useManageOrdersTable";
import { Order } from "@amzn/ito-client";
import { AssignOrderModal } from "../AssignOrderModal";
import { CancelOrderModal } from "../CancelOrderModal";
import { CompleteOrderModal } from "../CompleteOrderModal";
import { RecallLicenseModal } from '../RecallLicenseModal';
import { useFlashBarItemsActions } from "common/UseFlashBarItems/useFlashBarItems";
import { ConfirmActionModal } from "common/ConfirmActionModal/ConfirmActionModal";
import { CancelableEventHandler, ClickDetail } from "@amzn/awsui-components-react/polaris/internal/events";
import { CreateOrderModal } from "../CreateOrderModal/CreateOrderModal";
import { OrderStatus } from "configuration/config";

const SYSTEM = "SYSTEM";

export const ManageOrdersTableHeader = (props: {
    totalResults: number;
    selectedItems: Order[];
    setSelectedItems: React.Dispatch<React.SetStateAction<Order[]>>;
    showSelectedOnly: boolean;
    setShowSelectedOnly: (value: boolean) => void;
    procurementMembersSelectOptions: SelectProps.Option[];
    refreshData: (clearPreviousData: boolean) => void;
    useFlashBarItemsActions: useFlashBarItemsActions;
    onDownloadOrders: () => Promise<void>;
    onDownloadFilteredData?: () => void;
    isExportingFilteredData?: boolean;
    fileId?: string;
    fileName?: string;
}): JSX.Element => {
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [showCancelModal, setCancelShowModal] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showRecallLicenseModal, setRecallLicenseModal] = useState(false);
    const [showConfirmDownloadNewOrders, setConfirmDownloadNewOrders] = useState(false);
    // Non-null while the complete flow is active; the head is the order shown in CompleteOrderModal.
    const [completeQueue, setCompleteQueue] = useState<Order[] | null>(null);
    const [completeQueueTotal, setCompleteQueueTotal] = useState(0);
    // Tracks whether any order in the current sequence actually completed, to avoid a clearing refresh when every order was only skipped.
    const anyOrderCompletedRef = useRef(false);
    const { useFlashBarItemsActions, procurementMembersSelectOptions } = props;

    let selectionState: SelectionState;
    if (props.selectedItems.length == 0) {
        selectionState = SelectionState.NONE;
    } else if (props.selectedItems.length == 1) {
        selectionState = SelectionState.ONE;
    } else {
        selectionState = SelectionState.MULTIPLE;
    }

    /* istanbul ignore next */
    const onAssignOrders = () => {
        setShowAssignModal(true);
    };

    /* istanbul ignore next */
    const onCreateOrder = () => {
        setShowCreateModal(true);
    };

    /* istanbul ignore next */
    const dismissAssignModal = (shouldRefreshTable: boolean = false) => {
        if (shouldRefreshTable) {
            props.refreshData(true);
        }
        setShowAssignModal(false);
    };

    /* istanbul ignore next */
    const dismissCreateOrderModal = (shouldRefreshTable: boolean = false) => {
        if (shouldRefreshTable) {
            props.refreshData(true);
        }
        setShowCreateModal(false);
    };

    /* istanbul ignore next */
    const onCancelOrder = () => {
        setCancelShowModal(true);
    };

    /* istanbul ignore next */
    const dismissCancelModal = (shouldRefreshTable: boolean = false) => {
        if (shouldRefreshTable) {
            props.refreshData(true);
        }
        setCancelShowModal(false);
    };

    /* istanbul ignore next */
    const onCompleteOrder = () => {
        anyOrderCompletedRef.current = false;
        setCompleteQueueTotal(props.selectedItems.length);
        setCompleteQueue(props.selectedItems);
    };

    /* istanbul ignore next */
    const dismissCompleteModal = (shouldRefreshTable: boolean = false, failed: boolean = false, abortQueue: boolean = false) => {
        const isMultiOrderFlow = completeQueueTotal > 1;
        const remaining = completeQueue!.slice(1);
        if (shouldRefreshTable) {
            anyOrderCompletedRef.current = true;
        }

        // "x" abandons the remaining queue outright instead of advancing.
        if (abortQueue) {
            setCompleteQueue(null);
            if (anyOrderCompletedRef.current) {
                props.refreshData(true);
            }
            return;
        }

        // A backend failure already shows its own error banner; only manual skip needs this notice.
        if (!shouldRefreshTable && !failed && isMultiOrderFlow && remaining.length > 0) {
            const skippedOrder = completeQueue![0];
            useFlashBarItemsActions.addInfoFlashBarItem?.(
                getString('manageOrders.completeModal.orderSkipped', {
                    orderReference: skippedOrder.orderReference || skippedOrder.orderId
                })
            );
        }

        if (remaining.length === 0) {
            setCompleteQueue(null);
            // A refresh is only needed if this order (or an earlier one in the sequence) actually completed.
            if (anyOrderCompletedRef.current) {
                props.refreshData(true);
            }
            return;
        }
        setCompleteQueue(remaining);
    };

    /* istanbul ignore next */
    const dismissDownloadNewOrdersModal = (shouldRefreshTable: boolean = false) => {
        if (shouldRefreshTable) {
            props.refreshData(true);
        }
        setConfirmDownloadNewOrders(false);
    };

    /* istanbul ignore next */
    const onConfirmDownloadNewOrders: CancelableEventHandler<ClickDetail> | undefined = async () => {
        dismissDownloadNewOrdersModal(false);
        await props.onDownloadOrders();
    };

    const onRecallLicense = () => {
      setRecallLicenseModal(true);
    };
  
    const dismissRecallLicense = (shouldRefreshTable: boolean = false) => {
      if (shouldRefreshTable) {
        props.refreshData(true);
      }
      setRecallLicenseModal(false);
    };


    const disableCancel =
        selectionState !== SelectionState.ONE ||
        props.selectedItems[0].status === OrderStatus.COMPLETED ||
        props.selectedItems[0].status === OrderStatus.CANCELLED;

    const disableComplete =
        selectionState === SelectionState.NONE ||
        props.selectedItems.some(
            order => order.status === OrderStatus.COMPLETED || order.status === OrderStatus.CANCELLED
        );

    const disableRecallLicense =
      selectionState !== SelectionState.ONE ||
      props.selectedItems[0].status !== OrderStatus.COMPLETED  ||
      props.selectedItems[0].assignedTo === SYSTEM;

    const disableAssigneeButton = (): boolean => {
        if (selectionState == SelectionState.NONE) return true;

        for (const order of props.selectedItems) {
            if ([OrderStatus.CANCELLED, OrderStatus.COMPLETED].includes(order.status as OrderStatus)) {
                return true;
            }
        }

        return false;
    };

    return (
        <SpaceBetween size="m">
            <Header
                data-testid="manage-orders-table-header"
                variant="awsui-h1-sticky"
                counter={`(${props.totalResults})`}
            >
                {props.fileId
                    ? `${getString("manageOrders.table.uploadedOrdersheaderTitle")} ${props.fileName}`
                    : getString("manageOrders.table.headerTitle")}
            </Header>
            <Box float="right">
                <SpaceBetween direction="horizontal" size="xs">
                    {!props.fileId && props.onDownloadFilteredData ? (
                        <Button
                            data-testid="download-filtered-data-button"
                            iconName="download"
                            loading={props.isExportingFilteredData}
                            onClick={props.onDownloadFilteredData}
                        >
                            {getString("manageOrders.table.downloadFilteredDataButton")}
                        </Button>
                    ) : undefined}
                    <Button data-testid="recall-license-button" variant="normal" onClick={onRecallLicense} disabled={disableRecallLicense}>
                        {getString('manageOrders.table.recallLicenseButton')}
                    </Button>
                    <Button data-testid="create-order-button" onClick={onCreateOrder}>
                        {getString("manageOrders.table.createOrderButton")}
                    </Button>
                    <Button
                        data-testid="assign-order-button"
                        disabled={disableAssigneeButton()}
                        onClick={onAssignOrders}
                    >
                        {getString("manageOrders.table.assignOrderButton")}
                    </Button>
                    <Button
                        data-testid="cancel-order-button"
                        onClick={onCancelOrder}
                        disabled={disableCancel}
                    >
                        {getString("manageOrders.table.cancelOrderButton")}
                    </Button>
                    <Button
                        data-testid="complete-order-button"
                        onClick={onCompleteOrder}
                        disabled={disableComplete}
                    >
                        {getString("manageOrders.table.completeOrderButton")}
                    </Button>
                    {props.fileId ? undefined : (
                        <Button
                            data-testid="download-new-orders-button"
                            variant="primary"
                            onClick={() => setConfirmDownloadNewOrders(true)}
                        >
                            {getString("manageOrders.table.downloadNewOrdersButton")}
                        </Button>
                    )}
                </SpaceBetween>
            </Box>
            <Box color="text-status-inactive">{getString("manageOrders.table.headerDescription")}</Box>
            {selectionState !== SelectionState.NONE && (
                <Box data-testid="selection-count-badge">
                    <SpaceBetween direction="horizontal" size="s">
                        <span>{getString("manageOrders.table.ordersSelected", { count: props.selectedItems.length })}</span>
                        <Toggle
                            data-testid="show-selected-only-toggle"
                            checked={props.showSelectedOnly}
                            onChange={({ detail }) => props.setShowSelectedOnly(detail.checked)}
                        >
                            {getString("manageOrders.table.showSelectedOnlyToggle")}
                        </Toggle>
                    </SpaceBetween>
                </Box>
            )}
            {showAssignModal && (
                <AssignOrderModal
                    procurementMembersSelectOptions={procurementMembersSelectOptions}
                    onDismiss={dismissAssignModal}
                    orders={props.selectedItems}
                    flashBarItemsActions={useFlashBarItemsActions!}
                />
            )}

            {showCreateModal && (
                <CreateOrderModal onDismiss={dismissCreateOrderModal} flashBarItemsActions={useFlashBarItemsActions!} />
            )}

            {showCancelModal && (
                <CancelOrderModal
                    showModal={showCancelModal}
                    onDismiss={dismissCancelModal}
                    flashBarItemsActions={useFlashBarItemsActions!}
                    selectedItem={props.selectedItems[0]}
                />
            )}

            {completeQueue && completeQueue.length > 0 && (
                <CompleteOrderModal
                    key={completeQueue[0].orderId}
                    showModal={true}
                    onDismiss={dismissCompleteModal}
                    flashBarItemsActions={useFlashBarItemsActions!}
                    selectedItem={completeQueue[0]}
                    progress={
                        completeQueueTotal > 1
                            ? { current: completeQueueTotal - completeQueue.length + 1, total: completeQueueTotal }
                            : undefined
                    }
                />
            )}

            {showRecallLicenseModal && (
                <RecallLicenseModal
                  showModal={showRecallLicenseModal}
                  onDismiss={dismissRecallLicense}
                  flashBarItemsActions={useFlashBarItemsActions!}
                  selectedItem={props.selectedItems[0]}
                />
            )}

            {showConfirmDownloadNewOrders && (
                <ConfirmActionModal
                    isProcessing={false}
                    visible={showConfirmDownloadNewOrders}
                    modalTitle={getString("manageOrders.table.downloadNewOrdersButton")}
                    onDismiss={dismissDownloadNewOrdersModal}
                    warningText={getString("manageOrders.table.confirmDownloadNewOrders")}
                    informationalText={getString("manageOrders.table.confirmDownloadNewOrdersInfo")}
                    onAction={onConfirmDownloadNewOrders}
                />
            )}
        </SpaceBetween>
    );
};
